import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import { Phone } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { NorwoodQuiz } from "@/components/site/NorwoodQuiz";
import { telHref } from "@/components/site/PhoneLink";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { HERO_IMG, PLAY_BTN, heroVideoUrl, mainPhone, pricingUrl, quickQuoteUrl } from "@/lib/site-core";
import {
  DOUG_PORTRAIT,
  DOUG_PORTRAIT_SRC,
  GOLD_CHECK_CIRCLE,
  GUARANTEE_IMG,
  NO_SHAVE_AFTER,
  NO_SHAVE_AFTER_SRC,
  NO_SHAVE_BEFORE,
  NO_SHAVE_BEFORE_SRC,
  doctors,
  results,
} from "@/lib/site-data";
import athletesAsset from "@/assets/restore/chicago-athletes.webp.asset.json";
import harrisScott from "@/assets/restore/dr-harris-scott.webp.asset.json";
import eddieOlczyk from "@/assets/restore/eddie-olczyk.png.asset.json";
import { breadcrumbSchema, safeJsonLd } from "@/lib/structured-data";
import { useLocalQuoteLinks } from "@/hooks/use-local-quote-links";
import { NYC_CITIES, NYC_FAQS, NYC_META, NYC_REVIEWS } from "@/content/wp-parity/pages/locations-nyc";

// /locations/nyc/ is the WordPress NYC page, rebuilt on 6 Oct 2026. It
// answered 301 to /locations/new-york/ after the move. Before the move it held
// position 25.5 with about 2,600 Google impressions a week. All text on this
// page is the WordPress copy (src/content/wp-parity/pages/locations-nyc.ts and
// the sections below), with typos fixed and empty hype words dropped. Claims,
// prices and statistics are unchanged. Do not edit the words without Brian's
// approval.

const Reviews = lazy(() => import("@/components/site/Reviews").then((m) => ({ default: m.Reviews })));
const DoctorsGrid = lazy(() => import("@/components/site/DoctorsGrid").then((m) => ({ default: m.DoctorsGrid })));
const Faq = lazy(() => import("@/components/site/Faq").then((m) => ({ default: m.Faq })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then((m) => ({ default: m.QuoteForm })));

const CANONICAL = "https://restorehair.com/locations/nyc/";

export const Route = createFileRoute("/locations/nyc")({
  head: () => ({
    meta: [
      { title: NYC_META.title },
      { name: "description", content: NYC_META.description },
      { property: "og:title", content: NYC_META.title },
      { property: "og:description", content: NYC_META.description },
      { property: "og:url", content: CANONICAL },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd(
          breadcrumbSchema([
            { name: "Locations", path: "/locations/" },
            { name: "New York", path: "/locations/new-york/" },
            { name: "New York City", path: "/locations/nyc/" },
          ]),
        ),
      },
    ],
  }),
  component: NycPage,
});

const BUTTON =
  "inline-block bg-primary px-8 py-4 text-sm font-bold tracking-wider text-primary-foreground transition hover:bg-primary/90";

// CallRail may swap this number before hydration: see PhoneLink.tsx. The
// spoken label is built from the link's own text ("Call " and the number are
// two text nodes of this element), so it follows the swap.
function CallButton() {
  return (
    <a
      href={telHref(mainPhone)}
      suppressHydrationWarning
      className="inline-flex items-center gap-2 border-2 border-foreground px-6 py-[14px] text-sm font-bold tracking-wider text-foreground transition hover:bg-foreground hover:text-background"
    >
      <Phone className="h-4 w-4" aria-hidden="true" />
      Call {mainPhone}
    </a>
  );
}

function ClientTag({ badge, name }: { badge: string; name: string }) {
  return (
    <figure className="text-center">
      <span className="hidden bg-[#F7E6BF] px-2 py-1 text-xs font-medium text-foreground md:inline-block md:text-sm">
        {badge}
      </span>
      <figcaption className="text-base font-bold text-foreground md:mt-1 md:text-lg">{name}</figcaption>
      <p className="text-xs italic text-muted-foreground md:text-sm">RESTORE Client</p>
    </figure>
  );
}

function NycHero() {
  const [open, setOpen] = useState(false);
  return (
    <section data-cta-location="hero" className="bg-background">
      <div className="mx-auto grid max-w-7xl items-end gap-10 px-4 pt-4 md:pt-6 lg:grid-cols-2 lg:px-8 lg:pt-8">
        <div className="pb-8 md:pb-10 lg:pb-12">
          <h1 className="text-sm font-bold tracking-wide text-foreground">RESTORE® | Hair Transplant NYC</h1>
          <h2 className="mt-3 text-5xl font-extrabold leading-tight text-foreground md:text-6xl">Get Your Hair Back!</h2>
          <h2 className="mt-5 text-xl font-medium text-foreground md:text-2xl">
            RESTORE your Confidence with the top hair restoration team in New York City
          </h2>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground">
            From celebrities and athletes to businessmen and everyman, RESTORE® Hair has performed over 20,000 hair
            restoration procedures, but you won’t recognize our work. That’s a good thing because the results are soft,
            natural, and undetectable.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            No hair plugs, no staples, no linear scarring. Just results.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href={quickQuoteUrl} className={BUTTON}>
              Get a Quick Quote
            </a>
            <CallButton />
          </div>
        </div>
        <div className="relative self-end">
          <div className="grid grid-cols-2 gap-2 px-2 pb-3">
            <ClientTag badge="Hall of Fame Football Player" name="Brian Urlacher" />
            <ClientTag badge="Hall of Fame Football Player" name="Deion Sanders" />
          </div>
          <img
            src={HERO_IMG}
            srcSet={`/img/hero-380.webp 380w, ${HERO_IMG} 507w`}
            sizes="(max-width: 768px) 100vw, 507px"
            alt="Brian Urlacher and Deion Sanders"
            width={507}
            height={447}
            decoding="async"
            className="block h-auto w-full"
          />
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Play video"
            className="absolute left-1/2 top-[68%] -translate-x-1/2 -translate-y-1/2 md:top-1/2"
          >
            <img loading="lazy" decoding="async" src={PLAY_BTN} alt="" width={184} height={184} className="h-16 w-16 md:h-20 md:w-20" />
          </button>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl border-0 bg-black p-0">
          <VisuallyHidden>
            <DialogTitle>Video player</DialogTitle>
          </VisuallyHidden>
          <div className="aspect-video w-full">
            {open && <video src={heroVideoUrl} controls autoPlay className="h-full w-full" playsInline />}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function NycWhyChoose() {
  return (
    <section className="bg-background pt-10 md:pt-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
          <p className="text-4xl font-extrabold leading-tight text-foreground md:text-5xl">
            <span className="font-light">Why Choose </span>
            <br />
            RESTORE Hair?
          </p>
          <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
            For the past 20 years, men just like you in NYC and across the United States, have reached out to RESTORE for
            a hair transplant. That’s because RESTORE’s team of{" "}
            <a href="/doctors/" className="text-foreground underline hover:text-primary">
              elite level hair doctors
            </a>{" "}
            can deliver a thick full head of hair through the RESTORE method. At RESTORE Hair, we deliver natural and
            virtually undetectable results. We’ve come a long way in the world of hair restoration, and thanks to
            RESTORE, there’s never been a better time to get your hair back.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-4 gap-2 sm:gap-4 md:gap-6">
          <ClientTag badge="Hall of Fame Hockey Player" name="Eddie Olczyk" />
          <ClientTag badge="Hall of Fame Football Player" name="Deion Sanders" />
          <ClientTag badge="Hall of Fame Football Player" name="Brian Urlacher" />
          <ClientTag badge="Professional Baseball Player" name="Ian Happ" />
        </div>
        <img
          src={athletesAsset.url}
          alt="Eddie Olczyk, Deion Sanders, Brian Urlacher and Ian Happ, RESTORE clients"
          width={1280}
          height={400}
          loading="lazy"
          decoding="async"
          className="mt-4 block h-auto w-full"
        />
      </div>
    </section>
  );
}

function NycResults() {
  return (
    <section className="bg-background py-10 md:py-14">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <p className="text-center text-sm font-semibold uppercase tracking-wide text-foreground">BRAGGING RIGHTS</p>
        <h4 className="mt-2 text-center text-sm font-semibold uppercase tracking-wide text-foreground">
          Before &amp; After Pictures of Hair Transplants
        </h4>
        <h2 className="mx-auto mt-3 max-w-5xl text-center text-3xl font-extrabold leading-none text-foreground md:text-5xl">
          Real Clients, Real Results
        </h2>
        <p className="mx-auto mt-6 max-w-4xl text-center text-base leading-relaxed text-muted-foreground">
          Lasting results are the most significant indicator of a successful RESTORE® No-Shave FUE™ Hair Transplant
          procedure. From celebrities and athletes to businessmen and every man, RESTORE's hair transplant before and
          after results are a testament to our expertise with FUE procedures that do not require shaving your head.
        </p>
        <p className="mx-auto mt-3 max-w-4xl text-center text-sm italic text-muted-foreground">
          100% authentic RESTORE clients. No photoshopping. No trick photography. No Stock photos.
        </p>
        <div className="mt-12 grid gap-[15px] sm:grid-cols-2 lg:grid-cols-4">
          {results.map((r) => (
            <div key={r.name} className="flex flex-col overflow-hidden bg-background shadow-sm">
              <div className="grid h-[250px] w-full grid-cols-2 overflow-hidden bg-white">
                <img src={r.before} alt={`${r.name} before hair transplant`} loading="lazy" width={500} height={500} className="block h-full w-full object-cover object-center" />
                <img src={r.after} alt={`${r.name} after hair transplant`} loading="lazy" width={500} height={500} className="block h-full w-full object-cover object-center" />
              </div>
              <div className="min-h-[139px] bg-foreground/70 px-[15px] py-[6px] text-background">
                <p className="text-[15px] font-bold leading-6">{r.grafts}</p>
                <h2 className="text-[30px] font-normal leading-[40px]">
                  {r.href ? (
                    <a href={new URL(r.href, "https://restorehair.com").pathname} className="underline underline-offset-4">
                      {r.name}
                    </a>
                  ) : (
                    r.name
                  )}
                </h2>
                <p className="mt-[1px] text-base italic leading-5">
                  {r.role}
                  <br />
                  RESTORE® Client
                </p>
              </div>
            </div>
          ))}
        </div>
        <p className="mx-auto mt-8 max-w-5xl text-center text-xs italic leading-relaxed text-muted-foreground">
          Not all patients will experience the outcomes shown in the photographs and described in the testimonial
          statements on this website, and not all persons will be eligible for the procedure. Results will depend on a
          number of factors, including your personal medical history. Please ask a physician about your individual case.
          Testimonials were compensated for their endorsements.
        </p>
      </div>
    </section>
  );
}

const VALUE_PROPS = [
  {
    title: "100% Natural, Undetectable Hair Restoration Results",
    body: "You will be back to the hairline you remember in no time. No one will know you got the procedure done, but you. Get a fuller, thicker head of your own hair, without ugly hair plugs or visible scar lines - just results.",
  },
  {
    title: "Non-Invasive Approach",
    body: "At RESTORE, your individual hair follicles are relocated to fill in thinning areas without having to shave your head. Other clinics harvest donor hair by stripping skin from the back of the head. At RESTORE you’ll get the results you want minus the scalpel and stitches.",
  },
  {
    title: "Comfortable Hair Transplant Procedure",
    body: "You'll be in and out the same day, with little or no downtime. Sit back, stream movies, browse the web or read a book. Spend the afternoon relaxing in a private suite while we RESTORE your hair.",
  },
];

function NycValueProps() {
  return (
    <section className="bg-background py-10 md:py-14">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 lg:grid-cols-2 lg:px-8">
        <figure>
          <img
            src={harrisScott.url}
            alt="Dr. James Harris, RESTORE's Medical Director, with Scott C., RESTORE client"
            width={665}
            height={575}
            loading="lazy"
            decoding="async"
            className="block h-auto w-full"
          />
          <figcaption className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <span>
              <a href="/results/deion-sanders/" className="font-bold text-foreground">
                Scott C.
              </a>
              <br />
              <em className="text-muted-foreground">RESTORE Client</em>
            </span>
            <span className="text-right">
              <a href="/results/brian-urlacher/" className="font-bold text-foreground">
                Dr. James Harris
              </a>
              <br />
              <em className="text-muted-foreground">RESTORE's Medical Director</em>
            </span>
          </figcaption>
        </figure>
        <div>
          <p className="text-4xl font-semibold leading-tight text-foreground md:text-5xl">
            There is No Better Way to Restore Your Hair in New York City
          </p>
          <div className="mt-8 space-y-6">
            {VALUE_PROPS.map((v) => (
              <div key={v.title} className="flex gap-4">
                <img loading="lazy" decoding="async" src={GOLD_CHECK_CIRCLE} alt="" width={61} height={65} className="mt-1 h-8 w-8 flex-shrink-0" />
                <div>
                  <p className="text-lg font-bold text-foreground">{v.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{v.body}</p>
                </div>
              </div>
            ))}
          </div>
          <a href={pricingUrl} className={`mt-8 ${BUTTON}`}>
            Hair Transplant Pricing
          </a>
        </div>
      </div>
    </section>
  );
}

function NycNoShave() {
  return (
    <section className="bg-[#F8F8F8] py-10 md:py-14">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="text-center">
          <h3 className="text-3xl leading-tight text-foreground md:text-4xl">The Future of Hair Restoration is Here</h3>
          <h3 className="text-3xl font-extrabold leading-tight text-foreground md:text-4xl">The RESTORE® No-Shave FUE™</h3>
          <h3 className="mt-8 text-2xl leading-tight text-foreground md:text-3xl">Why You Should Consider the</h3>
          <h3 className="text-2xl font-extrabold leading-tight text-foreground md:text-3xl">
            RESTORE® No-Shave FUE™ Hair Transplant
          </h3>
          <p className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-muted-foreground">
            The RESTORE®{" "}
            <a href="/no-shave-fue-hair-transplant/" className="font-semibold text-foreground underline hover:no-underline">
              No-Shave FUE™ Method
            </a>{" "}
            is a vast improvement over other methods. With this unique method, you don't need to shave your head.
            Instead, we trim a selected number of hairs, and then extract the specific number of those follicles to be
            transplanted. This is far superior to the typical process of shaving the donor in order to harvest the needed
            follicles.
          </p>
          <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
            This method was developed by RESTORE's Medical Director,{" "}
            <a href="/doctors/dr-james-harris/" className="font-semibold text-foreground underline hover:no-underline">
              Dr. James Harris
            </a>{" "}
            - a pioneer and leader in the hair replacement industry. Now you can get a hair transplant without shaving
            your head.
          </p>
        </div>
        <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:items-start">
          <div>
            <p className="text-lg font-extrabold text-foreground">Doug M.</p>
            <p className="text-sm italic text-muted-foreground">RESTORE™ Client</p>
            <img
              src={DOUG_PORTRAIT_SRC}
              srcSet={DOUG_PORTRAIT}
              sizes="(min-width: 1024px) 600px, 90vw"
              alt="Doug M., RESTORE client"
              width={778}
              height={849}
              loading="lazy"
              decoding="async"
              className="mt-4 block h-auto w-full"
            />
          </div>
          <div>
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              <figure className="text-center">
                <img src={NO_SHAVE_BEFORE_SRC} srcSet={NO_SHAVE_BEFORE} sizes="(min-width: 1024px) 320px, 45vw" alt="Donor hair before No-Shave FUE" width={1400} height={1403} loading="lazy" decoding="async" className="aspect-square w-full object-contain" />
                <figcaption className="mt-2 text-sm font-bold text-foreground">
                  Donor Hair:
                  <br />
                  Before Procedure
                </figcaption>
              </figure>
              <figure className="text-center">
                <img src={NO_SHAVE_AFTER_SRC} srcSet={NO_SHAVE_AFTER} sizes="(min-width: 1024px) 320px, 45vw" alt="Donor hair after No-Shave FUE" width={1400} height={1516} loading="lazy" decoding="async" className="aspect-square w-full object-contain" />
                <figcaption className="mt-2 text-sm font-bold text-foreground">
                  Donor Hair:
                  <br />
                  After Procedure
                </figcaption>
              </figure>
            </div>
            <div className="mt-6 bg-[#5a5a5a] px-8 py-10 text-center">
              <p className="text-2xl font-extrabold text-primary md:text-3xl">More Hair. Less Hassle.</p>
              <p className="mt-4 text-sm leading-relaxed text-white md:text-base">
                Grow thicker, fuller hair with the RESTORE
                <br />
                No-Shave FUE.
              </p>
            </div>
            <div className="mt-6 text-center">
              <a href={quickQuoteUrl} className={BUTTON}>
                Get a Quick Quote
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function NycGuarantee() {
  return (
    <section className="py-16 md:py-24" style={{ backgroundColor: "#f5f5f5" }}>
      <div className="mx-auto max-w-6xl px-4 text-center lg:px-8">
        <a href="/restore-pledge/" className="inline-block">
          <img loading="lazy" decoding="async" src={GUARANTEE_IMG} alt="RESTORE Hair Satisfaction Guarantee" width={220} height={197} className="mx-auto" style={{ width: 220, height: 197 }} />
        </a>
        <h3 className="mt-3 text-[28px] font-extrabold leading-tight text-foreground sm:text-4xl md:text-[48px]">
          Your Satisfaction is Our #1 Priority
        </h3>
        <h3 className="mt-3 text-[28px] font-semibold leading-[1.1] text-foreground sm:text-4xl md:text-[48px]">
          More Than 20,000+ Successful Procedures
        </h3>
        <p className="mt-4 text-base leading-relaxed text-foreground">
          We stand behind our quality and expertise 100%. If for any reason, you’re not happy with the results of your
          RESTORE hair transplant procedure, we’ll perform an additional procedure at no cost to you. For complete
          details, read the{" "}
          <a href="/restore-pledge/" className="font-bold text-foreground underline hover:no-underline">
            RESTORE Pledge
          </a>
          .
        </p>
      </div>
    </section>
  );
}

function NycPricing() {
  return (
    <section className="bg-background py-10 md:py-14">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <p className="text-center text-3xl font-extrabold leading-tight text-foreground md:text-4xl">
          New York City hair transplant: How much does it cost?
        </p>
        <div className="mt-12 grid items-center gap-10 lg:grid-cols-2">
          <div>
            <h3 className="text-4xl font-extrabold leading-tight text-foreground md:text-5xl">
              Save $1,000 on your FUE Hair Transplant
            </h3>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              A full head of hair pays off for a lifetime. Find out more by filling out the form below for a
              personalized, no-pressure quote. Our client advisors are available to answer all of your questions, and
              address your specific needs. We also have no-money down payment plans starting under $150 per month.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href={quickQuoteUrl} className={BUTTON}>
                Get a Quick Quote
              </a>
              <CallButton />
            </div>
            <p className="mt-4 text-xs italic text-muted-foreground">*The RESTORE standard procedure starts at 2,500 grafts</p>
          </div>
          <img
            src={eddieOlczyk.url}
            alt="Eddie Olczyk, U.S. Hockey Hall of Fame & RESTORE Client"
            width={640}
            height={480}
            loading="lazy"
            decoding="async"
            className="block h-auto w-full"
          />
        </div>
      </div>
    </section>
  );
}

function NycPhysicians() {
  return (
    <section className="bg-background pt-10 md:pt-14">
      <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
        <h3 className="text-4xl font-extrabold leading-tight text-foreground md:text-5xl">New York City Physicians</h3>
        <p className="mt-2 text-sm font-semibold uppercase tracking-wide text-foreground">Unmatched Expertise</p>
        <p className="mt-6 text-base leading-relaxed text-muted-foreground">
          An elite team of renowned surgeons lead RESTORE’s medical group. Unlike other physicians that add hair
          restoration to a long menu of offered services, RESTORE physicians are dedicated to hair restoration - and
          nothing else. Focusing their careers on being the best at just one thing immensely benefits our patients and
          their hair restoration outcomes. RESTORE is led by its Medical Director, Dr. James Harris; an award winning
          hair surgeon, renowned author, international public speaker, and the inventor of the ‘Harris S.A.F.E System’,
          which is the medical device used to perform{" "}
          <a href="/how-choose-hair-transplant-doctor/" className="text-foreground underline hover:text-primary">
            FUE by doctors
          </a>{" "}
          throughout the world.
        </p>
      </div>
      <div className="mx-auto mt-10 grid max-w-4xl gap-6 px-4 text-center sm:grid-cols-2 lg:px-8">
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            <a href="/doctors/dr-james-harris/" className="hover:underline">
              Dr. James Harris
            </a>
          </h2>
          <p className="text-sm italic text-muted-foreground">
            Medical Director &amp; Hair
            <br />
            Restoration Surgeon
          </p>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            <a href="/doctors/dr-shadia-saleh/" className="hover:underline">
              Dr. Shadia Saleh
            </a>
          </h2>
          <p className="text-sm italic text-muted-foreground">
            Hair Restoration
            <br />
            Surgeon
          </p>
        </div>
      </div>
    </section>
  );
}

const NYC_LOCATIONS = [
  { name: "Brooklyn Heights", page: "/locations/new-york/brooklyn-heights/", address: "300 Cadman Plaza W,", book: "/schedule-a-consult/virtual/", label: "Book a Virtual Consultation" },
  { name: "Manhattan", page: null, address: "240 W 37th St, 5th Floor", book: "/schedule-a-consult/manhattan/", label: "Book a Consultation" },
  { name: "Nassau County", page: "/locations/new-york/nassau-county/", address: "1979 Marcus Ave, Suite 210. Lake Success", book: "/schedule-a-consult/virtual/", label: "Book a Virtual Consultation" },
  { name: "Queens", page: "/locations/new-york/queens/", address: "118-35 Queens Blvd, Forest Hills Tower", book: "/schedule-a-consult/virtual/", label: "Book a Virtual Consultation" },
];

function NycLocations() {
  return (
    <section className="bg-muted py-10 md:py-14">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <p className="text-center text-sm font-semibold uppercase tracking-wide text-foreground">New York Hair Restoration</p>
        <h3 className="mt-3 text-center text-3xl font-extrabold leading-tight text-foreground md:text-5xl">
          Conveniently Located in New York City
        </h3>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {NYC_LOCATIONS.map((l) => (
            <div key={l.name} className="flex flex-col bg-background p-6">
              <p className="inline-block self-start border-b-2 border-primary pb-1 text-xl font-bold text-foreground">
                {l.page ? (
                  <a href={l.page} className="hover:underline">
                    {l.name}
                  </a>
                ) : (
                  l.name
                )}
              </p>
              <p className="mt-3 text-sm text-muted-foreground">{l.address}</p>
              <a href={l.book} className="mt-4 text-sm font-bold text-foreground underline hover:no-underline">
                {l.label}
              </a>
            </div>
          ))}
        </div>
        <h4 className="mt-12 text-center text-xl font-bold text-foreground">See All The Cities We Serve</h4>
        <ul className="mx-auto mt-6 grid max-w-5xl grid-cols-2 gap-x-6 gap-y-2 text-sm text-muted-foreground sm:grid-cols-3 lg:grid-cols-4">
          {NYC_CITIES.map((c, i) => (
            <li key={`${c}-${i}`}>{c}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function NycPage() {
  useLocalQuoteLinks();
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <NycHero />
        <NorwoodQuiz />
        <NycWhyChoose />
        <NycResults />
        <NycValueProps />
        <NycNoShave />
        <Suspense fallback={null}>
          <Reviews
            items={NYC_REVIEWS}
            eyebrow="Client testimonials about their hair transplants"
            description="RESTORE has changed the lives of thousands of people struggling with hair loss. Here's what they have to say about their results. No wonder our clients from New York City have chosen RESTORE as #1 for hair transplants."
          />
        </Suspense>
        <NycGuarantee />
        <NycPricing />
        <NycPhysicians />
        <Suspense fallback={null}>
          <DoctorsGrid doctors={doctors.filter((d) => ["Dr. James Harris", "Dr. Shadia Saleh"].includes(d.name))} />
        </Suspense>
        <NycLocations />
        <Suspense fallback={null}>
          <Faq items={NYC_FAQS} />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
